import { Component, inject } from '@angular/core';
import { Routes } from '@angular/router';
import { chainActivateGuards } from '@examdojo/angular/util';
import { authRoutes } from '@examdojo/auth';
import { SyllabusService, topicsTreeResolver } from '@examdojo/category/v2';
import { NotFoundComponent } from '@examdojo/core/not-found';
import { ReviewPermissionsService } from '@examdojo/review';
import { mapToVoid } from '@examdojo/rxjs';
import { ProfileComponent } from '@examdojo/user';
import { combineLatest, first } from 'rxjs';
import { RootUrlParts, SyllabusUrlParts, ValidateUrlParts } from './app.model';
import { AuthenticatedLayoutComponent } from './layout';
import { OverviewComponent } from './overview';
import { PaymentComponent } from './payment';
import { AuthGuard } from './routing/auth.guard';
import {
  redirectToLastActiveQuestionTypeGuard,
  redirectToLastDerivativeQuestionModeGuard,
  redirectToLastReferenceQuestionModeGuard,
} from './routing/redirect-to-last-active-mode.guard';
import { validationRolesGuard } from './routing/review-permissions.guard';
import { syllabusRedirectGuard } from './routing/syllabus-redirect.guard';
import { validateModePermissionsGuard } from './routing/validate-mode-permissions.guard';

import { paymentRequestsResolver } from './routing/payment-request-resolver';
import { userHourlyRateResolver } from './routing/user-hourly-rate-resolver';
import { SYLLABUS_SLUG_PARAM } from './routing/validation-routing.model';
import { validationSyllabusesResolver } from './routing/validation-syllabuses-resolver';
import { reviewActiveItemResolver } from './validation-shell/review-active-item-resolver';
import { ValidationShellComponent } from './validation-shell/validation-shell.component';
import { DerivativeQuestionValidationComponent } from './validation/derivative-question-validation/derivative-question-validation.component';
import { RefQuestionValidationComponent } from './validation/ref-question-validation/ref-question-validation.component';

@Component({
  template: '',
  standalone: true,
})
export class RedirectComponent {}

export const appRoutes: Routes = [
  {
    path: '',
    component: AuthenticatedLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: RootUrlParts.Overview,
      },
      {
        path: RootUrlParts.Overview,
        component: OverviewComponent,
        resolve: {
          syllabuses: () => {
            const syllabusService = inject(SyllabusService);
            const reviewPermissionsService = inject(ReviewPermissionsService);
            return combineLatest([
              syllabusService.fetchSyllabuses(),
              reviewPermissionsService.fetchUserPermissions(),
            ]).pipe(first(), mapToVoid());
          },
        },
      },
      {
        path: RootUrlParts.Payment,
        component: PaymentComponent,
        resolve: {
          hourlyRate: userHourlyRateResolver,
          paymentRequests: paymentRequestsResolver,
        },
      },
      {
        path: RootUrlParts.Profile,
        component: ProfileComponent,
      },
      {
        path: RootUrlParts.Syllabus,
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: RedirectComponent,
            canActivate: [chainActivateGuards(validationRolesGuard, syllabusRedirectGuard)],
          },
          {
            path: `:${SYLLABUS_SLUG_PARAM}`,
            resolve: {
              syllabuses: validationSyllabusesResolver,
              topicsTree: topicsTreeResolver,
            },
            canActivate: [chainActivateGuards(validationRolesGuard, syllabusRedirectGuard)],
            component: ValidationShellComponent,
            children: [
              {
                path: '',
                pathMatch: 'full',
                redirectTo: SyllabusUrlParts.Validate,
              },
              {
                path: SyllabusUrlParts.Validate,
                resolve: {
                  reviewActiveItem: reviewActiveItemResolver,
                },
                children: [
                  {
                    path: '',
                    pathMatch: 'full',
                    canActivate: [redirectToLastActiveQuestionTypeGuard],
                    component: RedirectComponent,
                  },
                  {
                    path: ValidateUrlParts.DerivativeQuestion,
                    children: [
                      {
                        path: '',
                        canActivate: [redirectToLastDerivativeQuestionModeGuard],
                        pathMatch: 'full',
                        component: RedirectComponent,
                      },
                      {
                        path: ':mode',
                        canActivate: [validateModePermissionsGuard],
                        component: DerivativeQuestionValidationComponent,
                      },
                    ],
                  },
                  {
                    path: ValidateUrlParts.ReferenceQuestion,
                    children: [
                      {
                        path: '',
                        canActivate: [redirectToLastReferenceQuestionModeGuard],
                        pathMatch: 'full',
                        component: RedirectComponent,
                      },
                      {
                        path: ':mode',
                        canActivate: [validateModePermissionsGuard],
                        component: RefQuestionValidationComponent,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  ...authRoutes,

  {
    path: '**',
    component: NotFoundComponent,
  },
];
